import type { ReleaseAudience, ReleaseConfidence, ReleaseEntry } from "../../../web/lib/releaseNotes";

type HistoricalRelease = Omit<ReleaseEntry, "version">;

function historicalRelease(
  date: string,
  title: string,
  summary: string,
  items: string[],
  options: {
    audience?: ReleaseAudience[];
    confidence?: ReleaseConfidence;
    detailTitle?: string;
  } = {},
): HistoricalRelease {
  return {
    date,
    title,
    summary,
    confidence: options.confidence ?? "verified",
    audience: options.audience ?? ["customer", "admin", "system"],
    highlights: items.slice(0, 3),
    details: [{
      title: options.detailTitle ?? "Tamamlanan kapsam",
      items,
    }],
  };
}

// These entries reconstruct the completed work recorded in the two long-running
// support and development threads. They intentionally omit passwords, tokens,
// customer contact details and other operational secrets.
export const releaseNotesBackfill20260731To20260923: HistoricalRelease[] = [
  historicalRelease(
    "2026-07-31",
    "Sürüm Geçmişi Otomatik ve Denetlenebilir Hale Getirildi",
    "Dağınık geliştirme kayıtlarını tek sürüm geçmişinde toplamak için changelog üretimi, güven düzeyi ve hedef kitle bilgileri standartlaştırıldı.",
    [
      "Sürüm kayıtları başlık, tarih, güven düzeyi, hedef kitle, özet ve ayrıntı alanlarıyla ortak bir yapıya geçirildi.",
      "Eski görev ve Git geçmişinden sürüm notu üretmek için tekrar çalıştırılabilir dışa aktarma araçları hazırlandı.",
      "Müşteri ve yönetici paneli aynı sürüm kaynağını kullanacak şekilde birleştirildi.",
    ],
    { audience: ["admin", "system"] },
  ),
  historicalRelease(
    "2026-07-31",
    "Afterbuy Mağaza ve Kanal Görünürlüğü Tamamlandı",
    "Afterbuy mağazaları, sipariş kaynakları ve platform görselleri panelin diğer pazar yeri bağlantılarıyla aynı sunum modeline alındı.",
    [
      "Afterbuy platform tipi sipariş, mağaza, ürün önizleme ve fatura akışlarında tanındı.",
      "Orijinal platform logosu ve mağaza filtreleri müşteri panelindeki ortak platform listesine eklendi.",
      "Sipariş kaynağı ve satış kanalı bilgileri Afterbuy kayıtlarında korunacak şekilde veri modeli genişletildi.",
    ],
  ),
  historicalRelease(
    "2026-08-01",
    "Aktif ve Pasif Mağazalar Ayrı Gösteriliyor",
    "Bağlantısı kapatılan mağazaların kaybolduğu görünüm düzeltildi; mağazalar aktif ve pasif bölümlerinde ayrı yönetilebilir hale getirildi.",
    [
      "Pasif mağazalar müşteri mağaza ekranından artık gizlenmiyor.",
      "Aktif ve pasif bağlantılar ayrı başlıklarla gösterilerek yanlış mağazada işlem yapma riski azaltıldı.",
      "Mağaza durum sunumu için bağımsız yardımcılar ve regresyon testleri eklendi.",
    ],
  ),
  historicalRelease(
    "2026-08-04",
    "Afterbuy Tarih ve Zamanlayıcı Sapmaları Düzeltildi",
    "Afterbuy siparişlerinin saat dilimi nedeniyle atlanması ve zamanlayıcının bir sonraki çalışmayı yanlış göstermesi kalıcı olarak giderildi.",
    [
      "Berlin saati ve API zaman damgaları aynı kuralla karşılaştırılarak tarih aralığındaki sınır siparişler korunur hale getirildi.",
      "ModDate ve sipariş tarihindeki saat dilimi farkı nedeniyle oluşan eksik içe aktarımlar düzeltildi.",
      "Çalışıyor durumundaki mağazaların sonraki kontrol zamanı dakika sınırına göre güvenli biçimde hesaplanıyor.",
    ],
  ),
  historicalRelease(
    "2026-08-04",
    "Fatura Vergi Kimliği ve Alıcı Adresi Güçlendirildi",
    "Shopify ödeme kayıtlarından vergi kimliği ve fatura adresi daha güvenilir okunarak KenPost faturasına aktarılır hale getirildi.",
    [
      "Vergi kimliği Shopify siparişinin fatura adresi, ek alanları ve müşteri profilindeki güvenli kaynaklardan çözümlenir.",
      "Fatura alıcı anlık görüntüsü ile canlı PDF üretimi aynı adres seçme kuralını kullanır.",
      "Müşteri profilinde saklanan fatura bilgileri ve yönetici önizlemesi aynı sonuçla doğrulandı.",
    ],
  ),
  historicalRelease(
    "2026-08-04",
    "Adres ve Kargo Durumu Normalizasyonu Birleştirildi",
    "Alman adres sırası, şirket ve c/o alanları ile kargo hareket durumları ortak kurallarla işlenmeye başladı.",
    [
      "Almanya adreslerinde sokak ve kapı numarası doğru sıraya alınırken özel karakterler korunur.",
      "Şirket, adres eki ve alıcı adı DHL alanlarına kayıp oluşturmadan dağıtılır.",
      "İptal ve yeniden yayınlama kararları yalnız gerçek taşıyıcı hareket kanıtına göre verilir.",
    ],
  ),
  historicalRelease(
    "2026-08-04",
    "Yeni Sürüm Bildirimi ve Önbellek Yenileme Onarıldı",
    "Yayın sonrasında eski arayüz dosyasında kalan tarayıcılar için sürüm bildirimi ve güvenli yeniden yükleme akışı düzeltildi.",
    [
      "Uygulama sürümü önbellekten değil güncel dağıtımdan doğrulanır.",
      "Yenile düğmesi yeni paketi zorlayarak eski JavaScript dosyasına geri dönmez.",
      "Sürüm karşılaştırma ve yeniden yükleme davranışı otomatik testlerle koruma altına alındı.",
    ],
  ),
  historicalRelease(
    "2026-08-05",
    "Müşteri Panelinin Almanca ve İngilizce Kapsamı Tamamlandı",
    "Müşteri panelindeki sabit ve dinamik metinler baştan sona taranarak Almanca ve İngilizce eksikler giderildi.",
    [
      "Sipariş, mağaza, etiket, ödeme, profil ve hata akışlarındaki çevrilmemiş metinler tamamlandı.",
      "Dinamik durum ve hata mesajları ortak çeviri katmanından geçecek şekilde düzenlendi.",
      "Yeni Türkçe metinlerin diğer dillerde unutulmasını engelleyen kapsam testleri eklendi.",
    ],
  ),
  historicalRelease(
    "2026-08-06",
    "Shopify Faturası İptal ve Yeniden Düzenleme Akışı Eklendi",
    "Yanlış vergi veya alıcı bilgisiyle oluşan Shopify etiket faturaları yönetici panelinden güvenli biçimde iptal edilip yeniden düzenlenebilir hale geldi.",
    [
      "Manuel Shopify faturalarında iptal, yeniden oluşturma ve önizleme işlemleri tek yönetim akışında toplandı.",
      "Net, brüt ve vergi oranı hesapları fiyatlandırma türüne göre normalize edildi.",
      "İptal edilen belgenin kaydı korunurken yeni fatura ayrı ve izlenebilir bir belge olarak oluşturulur.",
    ],
  ),
  historicalRelease(
    "2026-08-06",
    "Toplu Etiket Kredisi İşlemleri Hızlandırıldı",
    "PDF doğrulaması nedeniyle zaman aşımına uğrayan toplu kredi tanımlama işlemleri ön doğrulama ve kontrollü eşzamanlılıkla hızlandırıldı.",
    [
      "Daha önce doğrulanmış etiket kaynakları yeniden ağır PDF kontrolüne alınmadan kullanılabilir.",
      "S3 okuma ve PDF doğrulama işlemleri sınırlı paralellikle yürütülür.",
      "Uzun süren yönetici işlemlerinin yanlışlıkla tekrar başlatılmasını önleyen işlem kaydı korunur.",
    ],
    { audience: ["admin", "system"] },
  ),
  historicalRelease(
    "2026-08-07",
    "Afterbuy Yerel Fatura İş Akışı Hazırlandı",
    "Afterbuy siparişlerinde platformun kendi fatura sürecini tetikleyen, tekrar ve hata korumalı yerel iş akışı tamamlandı.",
    [
      "Mağaza bazında yerel fatura akışı açılıp kapatılabilir ve tetik durumu kaydedilir.",
      "Geçici API hataları kontrollü olarak yeniden denenir; kalıcı hatalar açık durumla saklanır.",
      "Amazon e-posta ve pazar yeri fatura kurallarıyla çakışan kayıtlar güvenli biçimde atlanır.",
    ],
  ),
  historicalRelease(
    "2026-08-07",
    "eBay Yetki Yenileme Akışı Eklendi",
    "Süresi dolan veya geri alınan eBay erişimlerinde belirsiz senkron hatası yerine yeniden bağlantı yönlendirmesi ve anlaşılır durum gösterilir.",
    [
      "Geçersiz yenileme belirteci ayrı hata türü olarak tanınır.",
      "Müşteri mağaza ekranında eBay'i yeniden bağlama eylemi sunulur.",
      "Türkçe, Almanca ve İngilizce açıklamalar ile callback hata kontrolleri güncellendi.",
    ],
  ),
  historicalRelease(
    "2026-08-10",
    "Kargo Sağlığı ve Teslimat Sayıları Ortaklaştırıldı",
    "Genel bakış, rapor ve gönderi listelerinde farklı görünen teslimat sayıları tek taşıyıcı durum modeline bağlandı.",
    [
      "Teslim edildi, yolda, sorunlu ve taşıyıcıdan durum alınamadı sayıları aynı SQL kurallarını kullanır.",
      "DHL ve diğer taşıyıcıların terminal durumları ortak yardımcılarla sınıflandırılır.",
      "Yönetici ve müşteri ekranlarındaki özetlerin aynı gönderi kümesini saydığı test edildi.",
    ],
  ),
  historicalRelease(
    "2026-08-10",
    "Toplu Etiket Bakiyesi Atomik Hale Getirildi",
    "Yüksek adetli etiket tanımlamalarında bakiye özetinin geride kalmasına yol açan parça parça güncellemeler atomik toplu işleme geçirildi.",
    [
      "Etiket kaynakları ve müşteri bakiyesi tek tutarlı işlem sonucu üzerinden güncellenir.",
      "Aynı isteğin yinelenmesi ek kredi oluşturmadan mevcut işlem sonucunu döndürür.",
      "Onarım ve denetim için toplu işlem kaydı ile bakiye doğrulama sorguları eklendi.",
    ],
    { audience: ["admin", "system"] },
  ),
  historicalRelease(
    "2026-08-12",
    "Manuel Etikette Adres Bloğu Yapıştırma Geri Getirildi",
    "Tek parça adres metni manuel etiket formuna yapıştırıldığında ad, şirket, sokak, kapı numarası, posta kodu ve şehir alanları yeniden otomatik doldurulur.",
    [
      "Çok satırlı Almanya adresleri için bağımsız ve test edilebilir adres ayrıştırıcı eklendi.",
      "Yapıştırılan metin mevcut dolu alanları güvenli kurallarla tamamlar.",
      "Türkçe, Almanca ve İngilizce form açıklamaları güncellendi.",
    ],
  ),
  historicalRelease(
    "2026-08-12",
    "Etiketlenen Siparişleri Otomatik Arşivleme Eklendi",
    "Etiket oluşturulan siparişlerin listede gereksiz yere açık kalmasını önleyen mağaza bazlı otomatik arşivleme tamamlandı.",
    [
      "Sipariş etiketi başarıyla oluşturulduktan sonra tercih açıksa sipariş arşive alınır.",
      "Başarısız veya yarım kalan gönderiler otomatik arşivlenmez.",
      "Toplu ve tekli etiket akışları aynı arşivleme kuralını kullanır.",
    ],
  ),
  historicalRelease(
    "2026-08-12",
    "Afterbuy Bir Günlük Senkron ve Fatura Yeniden Denemesi Eklendi",
    "Afterbuy için kısa günlük çalışma aralığı ile fatura tetiklerinin dayanıklı yeniden deneme akışı tamamlandı.",
    [
      "Mağaza senkron aralığına son bir gün seçeneği eklendi.",
      "Fatura iş akışında deneme sayısı, son hata ve sonraki deneme zamanı kaydedilir.",
      "Aynı sipariş için tekrarlanan başarılı fatura tetikleri engellendi.",
    ],
  ),
  historicalRelease(
    "2026-08-13",
    "Sipariş Ürün ve Kalan Etiket Bilgileri Görünür Hale Geldi",
    "Yönetici müşteri ayrıntısında ürün satırları ve satın alınan etiketlerin kullanılan/kalan adetleri eksiksiz gösterilmeye başladı.",
    [
      "Ürün adı, SKU ve adet alanlarının dar ekran veya sütun ayarı nedeniyle kaybolması düzeltildi.",
      "Etiket alımlarında toplam, kullanılan ve kalan miktar ayrı gösterilir.",
      "Yoğun sipariş ve etiket sorgularındaki gereksiz taramalar azaltılarak CPU kullanımı düşürüldü.",
    ],
    { audience: ["admin", "system"] },
  ),
  historicalRelease(
    "2026-08-17",
    "Toplu Etiket İndirme Akışı Büyük Dosyalara Hazırlandı",
    "Çok sayıda etikette zaman aşımı veya eksik arşiv oluşturan indirme akışı, akışkan ZIP üretimi ve tarayıcı yedeğiyle güçlendirildi.",
    [
      "ZIP dosyaları bellekte tamamen biriktirilmeden akış halinde üretilir.",
      "İstek ve boyut sınırları kullanıcıya işlem başlamadan önce açıkça gösterilir.",
      "Sunucu birleştirmesi uygun değilse etiketler kontrollü parçalar halinde indirilebilir.",
    ],
  ),
  historicalRelease(
    "2026-08-17",
    "Etiket Satın Alma Düğmesi ve Ödeme Uzlaşması Düzeltildi",
    "Miktar seçildiği halde pasif kalan satın alma düğmesi ve Shopify ödeme durumunun geç yansıması giderildi.",
    [
      "Sepet miktarı, faturalama uygunluğu ve açılır ödeme penceresi durumu birbirinden ayrıldı.",
      "Ödenmiş ve kısmi ödenmiş Shopify siparişleri uzlaşma sırasında doğru sonuca geçirilir.",
      "Tekrarlanan ödeme bildirimi ikinci kez etiket kredisi oluşturmaz.",
    ],
  ),
  historicalRelease(
    "2026-08-21",
    "Billbee Adres ve Kapı Numarası Eşlemesi Düzeltildi",
    "Billbee siparişlerinde şirket, c/o, sokak ve kapı numarası alanlarının yanlış birleştiği adresler güvenli biçimde ayrıştırılmaya başladı.",
    [
      "Teslimat adresi fatura adresinden önce kullanılır ve şirket alanı alıcı adını ezmez.",
      "Alman kapı numarası desenleri harf ve ek içeren örneklerle doğrulanır.",
      "Eksik kapı numarası gerçek bir engelse kullanıcıya açık uyarı gösterilir.",
    ],
  ),
  historicalRelease(
    "2026-08-23",
    "Süresi Dolan Etiketler İçin Otomatik Telafi Eklendi",
    "Müşteriye atanmış ancak kullanılmadan süresi dolmuş etiketler tespit edilip ürün bazında telafi kuyruğuna alınır hale getirildi.",
    [
      "PDF içindeki doğrulanmış son kullanım tarihi envanter kaydına işlenir.",
      "Kullanılmamış süresi dolmuş etiket müşteriden düşülürken eşdeğer ürün için telafi geçmişi oluşturulur.",
      "Tarama ve telafi işlemi küçük gruplarla zamanlayıcı üzerinden devam eder.",
    ],
  ),
  historicalRelease(
    "2026-08-24",
    "Uzun Adresler DHL Alanlarına Güvenli Dağıtılıyor",
    "Uzun sokak ve adres eki nedeniyle etiket düğmesini kapatan kayıtlar, teslimat bilgisini kaybetmeden uygun DHL alanlarına bölünür hale getirildi.",
    [
      "Sokak ve kapı numarası korunur; taşan açıklama adres ekine aktarılır.",
      "Kısaltma teslimatı riske atacaksa otomatik işlem yerine düzenlenebilir uyarı gösterilir.",
      "Normalize edilen adres hem sipariş etiketinde hem manuel etiket akışında aynı kuralları kullanır.",
    ],
  ),
  historicalRelease(
    "2026-08-25",
    "Etiket Ödeme Penceresi Tarayıcı Engellerine Dayanıklı Hale Geldi",
    "Etiket satın alma sırasında açılmayan veya boş kalan ödeme penceresi için güvenli açılış ve aynı sekme yedeği eklendi.",
    [
      "Ödeme penceresi kullanıcı tıklaması sırasında açılarak tarayıcı popup engeline takılmaz.",
      "Yeni pencere açılamazsa ödeme adresi aynı sekmede güvenli biçimde açılır.",
      "Sepet miktarı ödeme dönüşünde yanlışlıkla sıfırlanmaz ve çift işlem oluşmaz.",
    ],
  ),
  historicalRelease(
    "2026-08-27",
    "Bozuk veya Erişilemeyen Etiket PDF'leri Otomatik Karantinaya Alınıyor",
    "404, S3 erişim ve PDF form hatası veren etiket kaynakları müşteri işleminden çıkarılıp sağlam kaynakla değiştirilecek şekilde onarıldı.",
    [
      "İndirilemeyen veya açılamayan PDF kaynağı teknik hata olarak işaretlenip yeniden dağıtıma kapatılır.",
      "Toplu işlem kalan sağlam etiketlerle devam eder ve başarısız kaynak için kredi kaybı oluşmaz.",
      "PDF içindeki sorunlu form ve JavaScript yapıları güvenli sayfa kopyalama yöntemiyle temizlenebilir.",
    ],
  ),
  historicalRelease(
    "2026-08-27",
    "Kaufland Sipariş ve Takip Geri Yazma Akışı Tamamlandı",
    "Kaufland siparişlerini alma, kargo bilgisini yazma ve sonucu doğrulama işlemleri ayrı ve yeniden denenebilir iş akışına geçirildi.",
    [
      "Kaufland API istekleri zaman aşımı, sayfalama ve hata sınıflandırmasıyla ortak yardımcıya taşındı.",
      "Etiket sonrası takip numarası ve taşıyıcı bilgisi siparişe idempotent biçimde yazılır.",
      "Başarısız takip aktarımları zamanlayıcıda diğer mağazaları durdurmadan yeniden denenir.",
    ],
  ),
  historicalRelease(
    "2026-08-27",
    "Referans Komisyon Yaşam Döngüsü Kuruldu",
    "Referans müşterilerinin hareketleri, hak ediş süreleri ve komisyon durumları geçmişi korunarak yeniden yapılandırıldı.",
    [
      "Bekleyen, onaylanan, kullanılan ve iptal edilen komisyonlar ayrı durumlarla izlenir.",
      "Mevcut referans müşterileri ve geçmiş kullanımlar için güvenli geriye dönük kayıt oluşturuldu.",
      "Varsayılan oran, müşteri özel oranı ve geçerlilik dönemi aynı hesaplama katmanında birleştirildi.",
    ],
  ),
  historicalRelease(
    "2026-08-28",
    "Referans Süresi ve Komisyon Oranı Geçmişe Uygulandı",
    "Referans ilişkisinin başlangıç/bitiş tarihi ile oran değişikliklerinin eski hareketleri bozması engellendi.",
    [
      "Komisyon her kullanımın gerçekleştiği tarihte geçerli olan oran ve süreyle hesaplanır.",
      "Oran değişikliği önceki hak edişleri geriye dönük olarak yeniden fiyatlandırmaz.",
      "Eksik referans geçmişi kontrollü toplu işlemle tamamlandı.",
    ],
  ),
  historicalRelease(
    "2026-08-28",
    "Takip Geri Yazma Kuyruğunda Hatalı Kayıt Önceliği Eklendi",
    "Daha önce başarısız olmuş takip aktarım kayıtlarının yeni kayıtlar arasında aç kalması engellendi.",
    [
      "Başarısız ve yeniden denenebilir kayıtlar sırada öncelik kazanır.",
      "Küçük eşzamanlı işçi grupları API limitlerini aşmadan birikmiş kuyruğu eritmeye devam eder.",
      "Başarılı, teslim edilmiş veya geri yazması kapalı kayıtlar gereksiz yeniden işlemden çıkarılır.",
    ],
  ),
  historicalRelease(
    "2026-08-29",
    "Kaufland Sipariş Durumları Doğru Eşleniyor",
    "Kaufland'ın platforma özel durumları KenPost açık, gönderildi, iptal ve iade durumlarına doğru çevrilir hale getirildi.",
    [
      "Durum toplama ve tekil durum okuma aynı normalizasyon yardımcısını kullanır.",
      "Gönderilmiş siparişler yeniden açık kuyruğa dönmez.",
      "Bir mağazadaki uzun onarım işlemi diğer mağaza senkronlarını bekletmez.",
    ],
  ),
  historicalRelease(
    "2026-08-31",
    "Shopify ve Amazon Siparişlerinde Ürün Ağırlığı Tamamlandı",
    "Sipariş satırında eksik gelen ağırlık, Shopify varyantı, SKU ve ürün metadatasından güvenli öncelik sırasıyla tamamlanır hale geldi.",
    [
      "Gram ve kilogram birimleri tek değere dönüştürülür; adetli ürünlerde toplam ağırlık doğru hesaplanır.",
      "Shopify Admin GraphQL varyant ağırlığı yalnız eksik satırlar için sorgulanır.",
      "Belirsiz ağırlıkta yanlış ürün seçmek yerine kullanıcı seçimi istenir.",
    ],
  ),
  historicalRelease(
    "2026-08-31",
    "Sipariş Listesi ve Özet Sorguları Hızlandırıldı",
    "Yüksek sipariş hacminde 500 hatası veren geniş sorgular sayfalama, seçici alanlar ve kısa süreli özet önbelleğiyle hafifletildi.",
    [
      "Açık, arşivlenmiş ve gönderilmiş siparişler yalnız gerekli kayıt aralığını okur.",
      "Özet sayaçları ana listeyi tekrar tekrar taramadan hesaplanır.",
      "Mağaza bazlı filtre ve otomatik arşiv kuralları sunucu sorgusuna uygulanır.",
    ],
  ),
  historicalRelease(
    "2026-08-31",
    "Bekleyen Etiketler ve S3 Hataları İçin Otomatik Onarım Eklendi",
    "Yarım kalan etiket üretimleri ile geçici depolama hataları zamanlayıcı tarafından güvenli biçimde yeniden işlenir hale getirildi.",
    [
      "Uzun süre pending kalan işlemler kilit süresi dolduğunda kontrollü yeniden denemeye alınır.",
      "S3 kaynağı bulunamayan etiketler yeniden doğrulanır ve uygun kaynaktan kurtarılır.",
      "Başarısız deneme diğer sipariş ve mağazaların zamanlayıcısını durdurmaz.",
    ],
  ),
  historicalRelease(
    "2026-08-31",
    "Amazon Takip ve Kaynak Durumu Kendi Kendini Onarıyor",
    "Shopify üzerinden gelen Amazon siparişlerinde takip yazıldığı halde KenPost durumunun geri açılması veya kaynakta eksik kalması önlendi.",
    [
      "Takip numarası bulunan gönderilmiş sipariş daha eski açık durumla ezilmez.",
      "Pazar yeri köprüsü başarısız olursa kaynak takip onarım kuyruğu güvenli yedek yolu dener.",
      "Arşivlenmiş ve tamamlanmış siparişler gereksiz tekrar aktarımından çıkarılır.",
    ],
  ),
  historicalRelease(
    "2026-09-01",
    "Komisyonu Etiket Kredisine Çevirme Cüzdanı Eklendi",
    "Onaylanmış referans komisyonları ürün ve adet seçilerek kısmen etiket kredisine dönüştürülebilir hale geldi.",
    [
      "Komisyon bakiyesi ile etiket maliyeti atomik cüzdan kaydında birlikte güncellenir.",
      "Kısmi kullanımda kalan komisyon tutarı korunur ve geçmiş hareketleri ayrı gösterilir.",
      "Yönetici onayı, müşteri talebi ve iptal durumları denetlenebilir kayıtlarla izlenir.",
    ],
  ),
  historicalRelease(
    "2026-09-01",
    "Etiket Yeniden Yayınlama ve Yükleme İşlemleri Dayanıklı Hale Geldi",
    "Uzun süren PDF yükleme veya yeniden yayınlama işlemlerinde donmuş görünen ekran ve mükerrer kayıtlar giderildi.",
    [
      "Yükleme işlemi ilerleme, zaman aşımı ve sonuç durumunu ayrı gösterir.",
      "Aynı etiket kaynağı ikinci kez yayımlanmaz; arşiv ve karantina durumları açıkça ayrılır.",
      "Maksimum adet ve kaynak dağılımı yönetici ekranında işlem öncesinde görülebilir.",
    ],
    { audience: ["admin", "system"] },
  ),
  historicalRelease(
    "2026-09-01",
    "Yönetici ve Müşteri Menüleri Yeniden Gruplandı",
    "Büyüyen özellik sayısında sayfa bulmayı kolaylaştırmak için yönetim ve müşteri menüleri işlev gruplarına ayrıldı.",
    [
      "Sipariş, etiket, finans, referans, rapor ve sistem alanları tutarlı başlıklarda toplandı.",
      "Rol ve yetkisi olmayan kullanıcıya ait bağlantılar menüde gösterilmez.",
      "Türkçe, Almanca ve İngilizce menü adları ile görünürlük testleri güncellendi.",
    ],
  ),
  historicalRelease(
    "2026-09-02",
    "Panel Genelinde Çeviri Tutarlılığı Sağlandı",
    "Yeni özelliklerle eklenen sabit ve dinamik metinler için yönetici ve müşteri panellerinde sistem çapında dil denetimi tamamlandı.",
    [
      "Navigasyon, gönderi, etiket, fatura ve hata metinleri ortak çeviri anahtarlarına taşındı.",
      "API hata kodları kullanıcı dilinde anlaşılır mesaja çevrilir.",
      "Eksik çeviri ve görünür Türkçe metinleri yakalayan otomatik kontroller genişletildi.",
    ],
  ),
  historicalRelease(
    "2026-09-02",
    "Amazon Gönderiminde DHL Taşıyıcı Uyumluluğu Düzeltildi",
    "Amazon'un kabul etmediği taşıyıcı adları DHL uyumlu değerlerle yeniden eşlenerek takip geri yazma hataları giderildi.",
    [
      "DHL, DHL Paket ve DHL Germany varyasyonları kaynak platformun kabul ettiği koda çevrilir.",
      "Başarısız Amazon fulfillment kaydı doğru taşıyıcıyla idempotent biçimde yeniden denenir.",
      "KenPost gönderildi durumu ancak kaynak doğrulaması veya güvenli kuyruk sonucu ile korunur.",
    ],
  ),
  historicalRelease(
    "2026-09-02",
    "Afterbuy Sipariş Görünürlüğü ve Toplu Onarım Tamamlandı",
    "Yanlış filtre nedeniyle gizlenen Afterbuy siparişleri yeniden görünür hale getirildi ve mevcut kayıtlar güvenli toplu işlemle onarıldı.",
    [
      "Kaynak sipariş durumu ile KenPost arşiv durumu birbirinden ayrıldı.",
      "Onarım istekleri Afterbuy API limitlerine uygun küçük gruplarla yürütüldü.",
      "Gizli, gönderilmiş ve iptal kayıtları için sonuçlar denetim geçmişinde saklandı.",
    ],
  ),
  historicalRelease(
    "2026-09-02",
    "Fatura Bağlantıları Her Ortamda Doğru Uygulamayı Açıyor",
    "Fatura PDF bağlantılarının eski veya yanlış alan adına gitmesi engellendi; bağlantılar güvenli uygulama temel adresinden üretilir hale geldi.",
    [
      "Sunucu tarafı fatura URL'si doğrulanmış uygulama adresini kullanır.",
      "Müşteri paneli mümkün olduğunda aynı origin üzerinden fatura indirmeyi tercih eder.",
      "Canlı ve test ortamı adreslerinin karışmasını önleyen testler eklendi.",
    ],
  ),
  historicalRelease(
    "2026-09-03",
    "Toplu Gönderi Oluşturma ve PDF Kurtarma Eklendi",
    "Sunucu süresi dolduğunda etiketi oluşmuş ancak sonuç listesine dönememiş gönderiler otomatik bulunup toplu indirmeye dahil edilir hale geldi.",
    [
      "Mevcut gönderi kaydı ve PDF hazırsa aynı sipariş için ikinci etiket üretilmez.",
      "Eksik PDF bağlantıları indirme öncesinde sınırlı yeniden denemeyle kurtarılır.",
      "Toplu sonuç, başarılı, kurtarılan ve gerçekten başarısız satırları ayrı gösterir.",
    ],
  ),
  historicalRelease(
    "2026-09-03",
    "Referans Hareket Geçmişi Ayrıntılandırıldı",
    "Referans müşterilerinin komisyon, etiket kredisi ve durum değişiklikleri tek kronolojik geçmişte görünür hale getirildi.",
    [
      "Müşteri, olay türü, tutar, oran ve bağlantılı kullanım bilgisi yönetici ekranında gösterilir.",
      "Eski kayıtlar referans ilişkisiyle yeniden eşlenirken mükerrer hareket oluşturulmaz.",
      "Filtreleme ve dışa aktarma için ortak geçmiş API'si eklendi.",
    ],
    { audience: ["admin", "system"] },
  ),
  historicalRelease(
    "2026-09-04",
    "Etiket PDF Tarihi, Yükleme Tarihi ve Son Kullanma Süresi Ayrıldı",
    "Etiket envanterinde PDF'nin gerçek oluşturulma tarihi, KenPost'a yüklenme tarihi ve doğrulanmış son kullanım süresi ayrı ayrı gösterilmeye başladı.",
    [
      "Son kullanma tarihi doğrulanamayan PDF'ler tahmin edilmeden Bilinmiyor olarak işaretlenir.",
      "Varsayılan sıra önce süresi yaklaşan, sonra en eski PDF olacak şekilde değiştirildi.",
      "Yönetici taraması ağır PDF'leri küçük arka plan gruplarında kontrol eder ve süresi dolan müşteri etiketlerini telafi kuyruğuna alır.",
      "Etiket ürünü ve ağırlığı son kullanma kontrolü sırasında otomatik değiştirilmez.",
    ],
  ),
  historicalRelease(
    "2026-09-21",
    "Etiket PDF Depolaması R2 Yedeğiyle Güçlendirildi",
    "Harici S3 hizmetindeki geçici 503 hatalarının tüm müşterilerde etiket indirmeyi durdurmaması için R2 okuma yedeği ve arka plan ısıtma akışı eklendi.",
    [
      "PDF önce birincil kaynaktan okunur; uygun kopya R2'de varsa kesinti sırasında oradan sunulur.",
      "Başarılı S3 okumaları kontrollü biçimde R2'ye önbelleklenir.",
      "Mevcut envanteri küçük gruplarla R2'ye taşıyan yönetici bakım işlemi eklendi.",
    ],
  ),
  historicalRelease(
    "2026-09-21",
    "Etiket Üretiminde Çift Kayıt ve Gecikmeli Sonuç Koruması Eklendi",
    "İlk isteği zaman aşımına uğrayıp daha sonra tamamlanan etiketlerde tekrar denemenin ikinci gönderi oluşturması engellendi.",
    [
      "Sipariş ve ürün bazlı işlem anahtarı aynı etiket isteğini tek sonuca bağlar.",
      "Bekleyen işlem tekrar denendiğinde önce mevcut gönderi ve PDF sonucu aranır.",
      "Başarısız görünen ancak taşıyıcıda oluşmuş etiketler kurtarılır; fazladan kredi tüketilmez.",
    ],
  ),
  historicalRelease(
    "2026-09-21",
    "Shopify Siparişlerinde Pazar Yeri Kimliği Yedekli Okunuyor",
    "Amazon gibi harici kanallardan Shopify'a gelen siparişlerin KenPost'ta kaybolmasına yol açan tek alan bağımlılığı kaldırıldı.",
    [
      "Pazar yeri sipariş kimliği etiketler, notlar, ek alanlar ve kanal bilgilerinden öncelik sırasıyla çözülür.",
      "Bir alanda bilgi eksikse diğer kaynaklar kullanılır; mevcut doğru eşleşme daha zayıf veriyle ezilmez.",
      "Kaçırılmış siparişler tekrar senkronla müşteri hesabına alınırken mükerrer KenPost siparişi oluşmaz.",
    ],
  ),
  historicalRelease(
    "2026-09-21",
    "Amazon Takip Onarımı 30 Dakikalık Programa Alındı",
    "Shopify/SalesTio üzerinden Amazon'a dönmesi gereken takip bilgileri için hedefli, eşzamanlılık kontrollü onarım görevi eklendi.",
    [
      "Takip geri yazma görevi mağaza senkronlarından bağımsız küçük gruplarla çalışır.",
      "DHL taşıyıcı adları Amazon uyumlu biçimde normalize edilir.",
      "Aynı siparişin başarılı takip kaydı tekrar gönderilmez; hata ve sonraki deneme zamanı kaydedilir.",
    ],
  ),
  historicalRelease(
    "2026-09-22",
    "Yeni PlentyONE Hesabı Uçtan Uca Doğrulandı",
    "PlentyONE API kullanıcısı, sipariş okuma ve takip yazma yetkileriyle müşteri hesabına bağlandı; gerçek sipariş verisi etiket oluşturmadan kontrol edildi.",
    [
      "API kullanıcı adı, parola ve rol yetkileri bağlantı testiyle doğrulandı.",
      "Sipariş, müşteri, ürün, adres, gerçek satış kanalı ve gönderim uygunluğu müşteri ekranında kontrol edildi.",
      "Köprü erişilemezse doğrudan PlentyONE bağlantısına güvenli dönüş yolu hazırlandı.",
      "Takip geri yazma ve 30 dakikalık otomatik senkron mağaza ayarlarıyla etkinleştirildi.",
    ],
  ),
  historicalRelease(
    "2026-09-23",
    "Müşteri Kullanımına Açık KenPost API v1 Yayınlandı",
    "Müşterilerin kendi yazılımlarından bakiye sorgulayıp manuel etiket oluşturabileceği, kapsam yetkili ve idempotent KenPost API v1 hazırlandı.",
    [
      "Müşteri paneline API anahtarı oluşturma, tek seferlik gizli anahtar gösterimi, iptal, süre ve IP kısıtı yönetimi eklendi.",
      "Hesap, yetenekler, bakiyeler, etiket ürünleri, doğrulama, etiket oluşturma, durum ve PDF uçları yayınlandı.",
      "Etiket oluşturma Idempotency-Key ile korunur; aynı istek tekrarlandığında ikinci kredi harcanmaz.",
      "OpenAPI belgesi, örnek istekler, kapsamlar, hata kodları ve güvenlik uyarıları müşteri ekranına eklendi.",
    ],
  ),
];
